import { type JwtPayload, STORAGE_KEYS } from '@shared/all/domains/trpc';
import {
  type DetailsCookiePayload,
  detailsCookieSchema,
  type EmailVerificationStatus
} from '@shared/all/domains/user';

/**
 * Parse a JWT token payload (used by mobile which reads JWT from AsyncStorage).
 */
export const parseJwt = (accessToken: string): JwtPayload | null => {
  const base64Url = accessToken.split('.')[1];
  const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
  if (!base64) return null;
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload) as JwtPayload;
};

/**
 * Read the client cookie value from document.cookie.
 * Returns the raw cookie value (truthy if logged in, empty string if not).
 * Name kept as getAcccessToken for backward compatibility with existing callers.
 */
export const getAcccessToken = () => {
  const cookies = typeof document === 'undefined' ? '' : document.cookie;
  if (!cookies.includes(STORAGE_KEYS.CLIENT_TOKEN)) return '';
  return cookies.split(STORAGE_KEYS.CLIENT_TOKEN + '=')[1]?.split(';')[0] || '';
};

/**
 * Extract the client cookie payload (userId + updatedAt) without verifying HMAC.
 * The server-side parseClientCookiePayload from @factiii/auth verifies the signature;
 * this client-side version only reads the JSON part for display purposes.
 */
export const readClientPayload = (
  cookieValue: string
): { userId: number; updatedAt: string } | null => {
  const dotIndex = cookieValue.indexOf('.');
  if (dotIndex === -1) return null;
  const data = cookieValue.slice(0, dotIndex);
  try {
    // base64url → standard base64 for atob
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as { userId: number; updatedAt: string };
  } catch {
    return null;
  }
};

// Returns actual ID for client (since cookies are synchronous) and 0 for mobile (since AsyncStorage is async)
export const getInitialUserId = (): number => {
  // Check if we're in a browser environment (client)
  if (typeof document !== 'undefined') {
    const cookieValue = getAcccessToken();
    if (!cookieValue) return 0;
    return readClientPayload(cookieValue)?.userId || 0;
  }

  // For mobile/React Native, return 0 since AsyncStorage is async
  return 0;
};

export const readFullClientPayload = (
  cookieValue: string
): DetailsCookiePayload | null => {
  const dotIndex = cookieValue.indexOf('.');
  if (dotIndex === -1) return null;
  const data = cookieValue.slice(0, dotIndex);
  try {
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const parsed = JSON.parse(json);
    const result = detailsCookieSchema.safeParse(parsed);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
};

export const buildUserFromCookieValue = (cookieValue: string) => {
  if (!cookieValue) return null;
  const payload = readFullClientPayload(cookieValue);
  if (!payload) return null;

  const STALE_COOKIE_MS = 24 * 60 * 60 * 1000;
  const updatedAt = new Date(payload.updatedAt).getTime();
  if (Date.now() - updatedAt > STALE_COOKIE_MS) return null;

  return buildUserShape(payload);
};

export const getInitialUserFromCookie = () => {
  if (typeof document === 'undefined') return null;
  const cookieValue = getAcccessToken();
  if (!cookieValue) return null;
  const payload = readFullClientPayload(cookieValue);
  if (!payload) return null;

  const STALE_COOKIE_MS = 24 * 60 * 60 * 1000;
  const updatedAt = new Date(payload.updatedAt).getTime();
  if (Date.now() - updatedAt > STALE_COOKIE_MS) return null;

  return buildUserShape(payload);
};

const buildUserShape = (payload: DetailsCookiePayload) => {
  return {
    isAuthenticated: true as const,
    id: payload.userId,
    username: payload.username,
    name: payload.name,
    email: payload.email,
    tag: payload.tag,
    avatarKey: payload.avatarKey,
    robohash: payload.robohash,
    hasPassword: payload.hasPassword,
    hasPasskey: payload.hasPasskey,
    twoFaEnabled: payload.twoFaEnabled,
    oauthProviders: payload.oauthProviders,
    oauthProvider: payload.oauthProvider,
    emailVerificationStatus:
      payload.emailVerificationStatus as EmailVerificationStatus,
    isPrivate: payload.isPrivate,
    isIncognito: payload.isIncognito,
    verificationRequestedAt: payload.verificationRequestedAt,
    deletionScheduledAt: payload.deletionScheduledAt,
    onboardingPath: payload.onboardingPath,
    bio: payload.bio ?? '',
    bannerKey: payload.bannerKey ?? '',
    tronsBalance: payload.tronsBalance
  };
};
